import { useParams } from 'react-router-dom';
import type { ApiError } from '@doccareplus/api-client';
import { useTranslation } from '@doccareplus/i18n';
import type { PrescriptionLayoutResponse } from '@doccareplus/ui/prescription';
import { Skeleton, ThemeToggle } from '@doccareplus/ui';
import { useSharedDocumentQuery } from '../application/sharedDocuments.query';
import type {
  OpenSharedDocumentResult,
  SharedInvoice,
  SharedPrescription,
  SharedReceipt,
} from '../infrastructure/sharedDocuments.api';
import { PortalPrescriptionDocument } from '@/shared/prescription/PortalPrescriptionDocument';

// Login-less guest viewer for a shared invoice / prescription / receipt. Rendered on the public /shared/:token
// route, outside ProtectedRoute and the PortalShell (no sign-out chrome). Renders from the immutable snapshot
// returned by the API — no live or internal clinic data.
export function SharedDocumentPage() {
  const { t } = useTranslation('portal');
  const { token } = useParams<{ token: string }>();
  const query = useSharedDocumentQuery(token);

  return (
    <div className="min-h-dvh bg-[var(--bg)] text-[var(--text-1)]">
      <header className="flex items-center justify-between px-4 py-3">
        <span className="text-[15px] font-bold text-[var(--brand-700)]">{t('appName', 'DocCare+')}</span>
        <ThemeToggle />
      </header>

      <main className="mx-auto w-full max-w-[720px] px-4 pb-16">
        {query.isLoading ? (
          <div role="status" aria-label={t('sharedDocuments.loading', 'Opening your document…')} className="mt-8">
            <span className="sr-only">{t('sharedDocuments.loading', 'Opening your document…')}</span>
            <div
              className="rounded-[16px] border border-[var(--border)] bg-[var(--surface)] p-5 shadow-[var(--shadow-1)]"
              aria-hidden
            >
              <div className="mb-4 border-b border-[var(--border)] pb-3">
                <Skeleton className="h-4 w-2/5" />
                <Skeleton className="mt-2 h-3 w-1/4" />
              </div>
              <div className="flex flex-col gap-2.5">
                <Skeleton className="h-3.5 w-full" />
                <Skeleton className="h-3.5 w-full" />
                <Skeleton className="h-3.5 w-4/5" />
                <Skeleton className="h-3.5 w-3/5" />
                <Skeleton className="mt-3 h-3.5 w-2/5" />
              </div>
            </div>
          </div>
        ) : query.isError ? (
          <StatusCard status={(query.error as unknown as ApiError | undefined)?.status} />
        ) : query.data ? (
          <DocumentBody data={query.data} />
        ) : null}

        <p className="mt-8 text-center text-[11.5px] text-[var(--text-3)]">
          {t('sharedDocuments.poweredBy', 'Shared securely via DocCare+')}
        </p>
      </main>
    </div>
  );
}

function StatusCard({ status }: { status: number | undefined }) {
  const { t } = useTranslation('portal');
  const variant =
    status === 410 ? 'expired' : status === 403 ? 'revoked' : 'notFound';

  return (
    <div className="mt-20 rounded-[16px] border border-[var(--border)] bg-[var(--surface)] p-8 text-center">
      <div className="mb-2 text-[40px]" aria-hidden>
        {variant === 'expired' ? '⏳' : variant === 'revoked' ? '🚫' : '🔗'}
      </div>
      <h1 className="mb-1 text-[17px] font-bold">{t(`sharedDocuments.${variant}Title`)}</h1>
      <p className="text-[13.5px] text-[var(--text-3)]">{t(`sharedDocuments.${variant}`)}</p>
    </div>
  );
}

function DocumentBody({ data }: { data: OpenSharedDocumentResult }) {
  if (data.documentType === 'Invoice' && data.invoice) {
    return <InvoiceView invoice={data.invoice} />;
  }
  if (data.documentType === 'Receipt' && data.receipt) {
    return <ReceiptView receipt={data.receipt} />;
  }
  if (data.documentType === 'Prescription' && data.prescription) {
    return <PrescriptionView prescription={data.prescription} layout={data.prescriptionLayout} />;
  }
  return <StatusCard status={404} />;
}

function money(value: number, currency: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
}

function DocShell({
  clinicName,
  title,
  children,
}: {
  clinicName: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <article className="rounded-[16px] border border-[var(--border)] bg-[var(--surface)] p-5 shadow-[var(--shadow-1)]">
      <div className="mb-4 border-b border-[var(--border)] pb-3">
        <div className="text-[18px] font-bold">{clinicName}</div>
        <div className="text-[12.5px] uppercase tracking-[0.06em] text-[var(--text-3)]">{title}</div>
      </div>
      {children}
    </article>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1 text-[13.5px]">
      <span className="text-[var(--text-3)]">{label}</span>
      <span className="font-medium text-[var(--text-1)]">{value}</span>
    </div>
  );
}

function InvoiceView({ invoice }: { invoice: SharedInvoice }) {
  const { t } = useTranslation('portal');
  return (
    <DocShell clinicName={invoice.clinicDisplayName} title={t('sharedDocuments.invoiceTitle')}>
      <Row label={t('sharedDocuments.invoiceNumber')} value={invoice.invoiceNumber} />
      <Row label={t('sharedDocuments.date')} value={new Date(invoice.invoiceDate).toLocaleDateString()} />
      <Row label={t('sharedDocuments.billedTo')} value={invoice.patientDisplayName} />

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-[var(--border)] text-left text-[var(--text-3)]">
              <th className="py-1.5 font-medium">{t('sharedDocuments.description')}</th>
              <th className="py-1.5 text-right font-medium">{t('sharedDocuments.qty')}</th>
              <th className="py-1.5 text-right font-medium">{t('sharedDocuments.unitPrice')}</th>
              <th className="py-1.5 text-right font-medium">{t('sharedDocuments.amount')}</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((line, i) => (
              <tr key={`${line.description}-${i}`} className="border-b border-[var(--border)]/60">
                <td className="py-1.5">{line.description}</td>
                <td className="py-1.5 text-right">{line.quantity}</td>
                <td className="py-1.5 text-right">{money(line.unitPrice, invoice.currency)}</td>
                <td className="py-1.5 text-right">{money(line.netAmount, invoice.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 border-t border-[var(--border)] pt-3">
        <Row label={t('sharedDocuments.subtotal')} value={money(invoice.subtotal, invoice.currency)} />
        {invoice.discountTotal > 0 && <Row label={t('sharedDocuments.discount')} value={`- ${money(invoice.discountTotal, invoice.currency)}`} />}
        {invoice.taxTotal > 0 && <Row label={t('sharedDocuments.tax')} value={money(invoice.taxTotal, invoice.currency)} />}
        <div className="mt-1 flex items-center justify-between border-t border-[var(--border)] pt-2 text-[15px] font-bold">
          <span>{t('sharedDocuments.total')}</span>
          <span>{money(invoice.total, invoice.currency)}</span>
        </div>
      </div>

      {invoice.paymentInstructions && (
        <p className="mt-4 rounded-[10px] bg-[var(--surface-2,transparent)] text-[12.5px] text-[var(--text-3)]">
          {invoice.paymentInstructions}
        </p>
      )}
    </DocShell>
  );
}

function ReceiptView({ receipt }: { receipt: SharedReceipt }) {
  const { t } = useTranslation('portal');
  return (
    <DocShell clinicName={receipt.clinicDisplayName} title={t('sharedDocuments.receiptTitle')}>
      <Row label={t('sharedDocuments.receiptNumber')} value={receipt.receiptNumber} />
      <Row label={t('sharedDocuments.date')} value={new Date(receipt.paymentDate).toLocaleDateString()} />
      <Row label={t('sharedDocuments.billedTo')} value={receipt.patientDisplayName} />
      <Row label={t('sharedDocuments.invoiceNumber')} value={receipt.invoiceNumber} />
      <Row label={t('sharedDocuments.paymentMethod')} value={receipt.method} />
      <div className="mt-2 flex items-center justify-between border-t border-[var(--border)] pt-2 text-[15px] font-bold">
        <span>{t('sharedDocuments.paid')}</span>
        <span>{money(receipt.amount, receipt.currency)}</span>
      </div>
      <Row label={t('sharedDocuments.balanceAfter')} value={money(receipt.balanceAfterPayment, receipt.currency)} />
    </DocShell>
  );
}

function PrescriptionView({
  prescription,
  layout,
}: {
  prescription: SharedPrescription;
  layout?: PrescriptionLayoutResponse | null;
}) {
  return <PortalPrescriptionDocument prescription={prescription} layout={layout} />;
}
